import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Stack, Chip, LinearProgress, Divider, Alert, Button,
} from '@mui/material';
import {
  AccountBalance, TrendingUp, WaterDrop, Sync, ReceiptLong, WarningAmber, Refresh,
} from '@mui/icons-material';
import { financeWorkspaceService, CommandCenter } from '../../services/financeWorkspaceService';
import { formatIndianNumber, formatCurrency } from '../../utils/currency';
import MoneyMovement from '../../components/finance/MoneyMovement';

const money = (v: number | undefined | null) => (v == null ? '—' : formatIndianNumber(Number(v)));
const num = (v: number | undefined | null, suffix = '') => (v == null ? '—' : `${Number(v).toLocaleString('en-IN')}${suffix}`);

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: string; onClick?: () => void; color?: string }> = ({ label, value, sub, onClick, color }) => (
  <Card variant="outlined" sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 3, borderColor: 'primary.main' } : {} }} onClick={onClick}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color, mt: 0.25 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
  </Stack>
);

const FinanceCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<CommandCenter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    financeWorkspaceService.commandCenter()
      .then(setData).catch((e) => setError(e.message || 'Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const p = data?.position || {}; const pr = data?.profitability || {}; const l = data?.liquidity || {};
  const wc = data?.workingCapital || {}; const tax = data?.tax || {}; const ex = data?.exceptions;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Finance Command Center</Typography>
          <Typography variant="body2" color="text.secondary">
            {data ? `${data.financialYear} · as of ${data.asOf}` : 'Live financial position, profitability, liquidity and exceptions'}
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mt: 2 }} />}

      <Box sx={{ mt: 2 }}><MoneyMovement /></Box>

      <SectionTitle icon={<AccountBalance fontSize="small" />} title="Financial Position" />
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Cash & Bank" value={money(p.cash)} onClick={() => navigate('/treasury/cash')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Receivables" value={money(p.receivables)} onClick={() => navigate('/finance/ageing')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Payables" value={money(p.payables)} onClick={() => navigate('/finance/ageing')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Inventory" value={money(p.inventory)} onClick={() => navigate('/inventory')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Fixed Assets" value={money(p.fixedAssets)} onClick={() => navigate('/assets')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Investments" value={money(p.investments)} onClick={() => navigate('/treasury/investments')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Debt" value={money(p.debt)} onClick={() => navigate('/treasury/loans')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Working Capital" value={money(p.workingCapital)} color="primary.main" /></Grid>
      </Grid>

      <SectionTitle icon={<TrendingUp fontSize="small" />} title="Profitability (FY to date)" />
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Revenue" value={money(pr.revenue)} onClick={() => navigate('/finance/reports')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="COGS + Purchases" value={money((pr.cogs || 0) + (pr.purchases || 0))} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Gross Profit" value={money(pr.grossProfit)} sub={pr.grossMarginPct != null ? `${pr.grossMarginPct}% margin` : ''} color="success.main" /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Net Profit" value={money(pr.netProfit)} sub={pr.netMarginPct != null ? `${pr.netMarginPct}% margin` : ''} color={(pr.netProfit || 0) >= 0 ? 'success.main' : 'error.main'} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionTitle icon={<WaterDrop fontSize="small" />} title="Liquidity" />
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 4 }}><Stat label="Current Ratio" value={num(l.currentRatio)} /></Grid>
            <Grid size={{ xs: 4 }}><Stat label="Quick Ratio" value={num(l.quickRatio)} /></Grid>
            <Grid size={{ xs: 4 }}><Stat label="Cash Ratio" value={num(l.cashRatio)} /></Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionTitle icon={<Sync fontSize="small" />} title="Working Capital" />
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 3 }}><Stat label="DSO" value={num(wc.dso, 'd')} /></Grid>
            <Grid size={{ xs: 3 }}><Stat label="DPO" value={num(wc.dpo, 'd')} /></Grid>
            <Grid size={{ xs: 3 }}><Stat label="Inv Days" value={num(wc.inventoryDays, 'd')} /></Grid>
            <Grid size={{ xs: 3 }}><Stat label="CCC" value={num(wc.cashConversionCycle, 'd')} /></Grid>
          </Grid>
        </Grid>
      </Grid>

      <SectionTitle icon={<ReceiptLong fontSize="small" />} title="Tax Position" />
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Output GST" value={money(tax.outputGst)} onClick={() => navigate('/finance/gst-reports')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Input GST (ITC)" value={money(tax.inputGst)} onClick={() => navigate('/finance/gst-reports')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Net GST Payable" value={money(tax.netGstPayable)} color="warning.main" onClick={() => navigate('/finance/gst-reports')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="TDS Payable" value={money(tax.tdsPayable)} onClick={() => navigate('/finance/tds-reports')} /></Grid>
      </Grid>

      <SectionTitle icon={<WarningAmber fontSize="small" />} title="Exceptions" />
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Stat label="Overdue Receivables" value={ex ? `${money(ex.overdueReceivables.amount)}` : '—'} sub={ex ? `${ex.overdueReceivables.count} invoices` : ''} color="error.main" onClick={() => navigate('/finance/collections')} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Stat label="Overdue Payables" value={ex ? `${money(ex.overduePayables.amount)}` : '—'} sub={ex ? `${ex.overduePayables.count} invoices` : ''} color="warning.main" onClick={() => navigate('/finance/ageing')} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Stat label="Unposted Journals" value={ex ? String(ex.unpostedJournals) : '—'} onClick={() => navigate('/finance/journal')} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Stat label="Pending Approvals" value={ex ? String(ex.pendingApprovals) : '—'} />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 3 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Every figure is derived live from the posted general ledger and sub-ledgers. Click a card to drill in.
      </Typography>
    </Box>
  );
};

export default FinanceCommandCenter;
